// ETHRAEON Docs page · Phase S
// Public-only. No internal canon exposure (no internal layer names, no forge identifiers, no chain anchors).
// (c) S. Jason Prohaska (ingombrante) 2024-2026

const SECTIONS = [
  {
    id: "introduction",
    title: "Introduction",
    body:
      "Ethraeon is a human-first orchestration system. It provides calm, legible, respectful tooling built to support decision-making by prioritizing clarity and agency over automation or prediction.",
  },
  {
    id: "orchestration",
    title: "Orchestration",
    body:
      "Ethraeon coordinates reasoning, boundary checks, traceability, and user-facing outputs into one calm interface. The orchestration layer keeps these tasks aligned, consistent, and understandable.",
  },
  {
    id: "shell",
    title: "Constitutional AI Shell",
    body:
      "The Constitutional AI Shell is a breathable boundary around model outputs. It helps keep decisions aligned with policy, context, and human agency.",
  },
  {
    id: "dim",
    title: "Decision Insight Model",
    body:
      "The Decision Insight Model surfaces structured options without dictating outcomes. It clarifies choices and tradeoffs in plain language.",
  },
  {
    id: "witness",
    title: "Recursive Witness OS",
    body:
      "Recursive Witness OS provides traceability without surveillance. It reflects process steps back to the user so actions can be understood and reviewed.",
  },
  {
    id: "architecture",
    title: "Architecture overview",
    body:
      "Ethraeon separates input interpretation, orchestration logic, insight modules, and the calm UI layer so the system remains stable, interpretable, and safe to deploy.",
  },
];

const GLOSSARY = [
  ["Constitutional AI Shell", "A boundary layer that applies rules and clarity around system outputs."],
  ["Decision Insight Model", "A tool that surfaces options, not predictions."],
  ["Recursive Witness OS", "A transparent record of process steps without surveillance."],
  ["Orchestration", "The coordination of reasoning, tone, boundary checks, and outputs."],
];

const FAQ = [
  ["Does Ethraeon replace decision-makers?", "No. Ethraeon clarifies decisions, but never makes them."],
  ["Is this predictive?", "No. It offers structured insight, not predictions or scores."],
  ["Does it collect behavioral data?", "No. It does not profile or score users."],
];

export function Docs() {
  return (
    <main className="min-h-screen bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-50">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl md:text-5xl font-light">Documentation</h1>
        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
          What Ethraeon does, how it is structured, and what it does not do.
        </p>

        <nav className="mt-8 flex flex-wrap gap-x-4 gap-y-1 text-sm text-neutral-500 dark:text-neutral-400">
          {SECTIONS.map((s) => (
            <a key={s.id} href={`#${s.id}`} className="hover:text-neutral-900 dark:hover:text-neutral-100">
              {s.title}
            </a>
          ))}
          <a href="#glossary" className="hover:text-neutral-900 dark:hover:text-neutral-100">Glossary</a>
          <a href="#faq" className="hover:text-neutral-900 dark:hover:text-neutral-100">FAQ</a>
        </nav>

        <div className="mt-12 space-y-12">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id}>
              <h2 className="text-2xl font-medium">{s.title}</h2>
              <p className="mt-3 text-neutral-700 dark:text-neutral-300">{s.body}</p>
            </section>
          ))}

          <section id="glossary">
            <h2 className="text-2xl font-medium">Glossary</h2>
            <dl className="mt-4 space-y-3">
              {GLOSSARY.map(([term, def]) => (
                <div key={term}>
                  <dt className="font-medium">{term}</dt>
                  <dd className="text-neutral-700 dark:text-neutral-300">{def}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section id="faq">
            <h2 className="text-2xl font-medium">FAQ</h2>
            <dl className="mt-4 space-y-4">
              {FAQ.map(([q, a]) => (
                <div key={q}>
                  <dt className="font-medium">{q}</dt>
                  <dd className="mt-1 text-neutral-700 dark:text-neutral-300">{a}</dd>
                </div>
              ))}
            </dl>
          </section>
        </div>
      </div>
    </main>
  );
}
